import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CompanyDirector } from '@app/modules/company/models/company-director.model';
import { Company } from '@app/modules/company/models/company.model';
import { Corporate } from '@app/modules/company/models/corporate.model';
import { OfficerCorporateFormType } from '@app/modules/officer/models/form/officer-corporate-form-type';
import { OfficerIndividualFormType } from '@app/modules/officer/models/form/officer-individual-form-type';
import { OfficerCompany } from '@app/modules/officer/models/officer-company.model';
import { OfficerPerson } from '@app/modules/officer/models/officer-person.model';
import { ServiceSupportedCountry } from '@app/modules/service/models/service-supported-country.model';
import { SupportedCountry } from '@app/modules/supported-country/models/supported-country.model';
import { User } from '@app/modules/user/models/user.model';
import {
  FileTypeEnum,
  JobType,
  OfficerCompanyType,
  OfficerPersonType,
  OfficerType,
  OfficerTypeEnum,
} from '@generated/graphql';
import { AbstractOfficerForm } from '@shared/classes/officer/abstract-officer-form.class';
import { ExistingOfficerState } from '@shared/classes/officer/existing-officer-state.class';
import { createOfficerForm } from '@shared/classes/officer/officer-form-factory';
import { OfficerInputHelpers } from '@shared/classes/officer/officer-input.helper';
import {
  DropzoneAction,
  DropzoneFileChangedEvent,
} from '@shared/component/common/dropzone-upload/dropzone-upload.component';
import { ExistsOfficerType } from '@shared/component/job/component/company-incorporation/dialog/add-officer-dialog/classes/exists-officer.type';
import { OfficerHelper } from '@shared/helpers/officer.helper';
import { OfficerRoleEnum } from '@shared/types/officerEnum';
import { DateTime } from 'luxon';
import { BehaviorSubject, Subject } from 'rxjs';
import { createOrderServiceState } from '@app/modules/job/services/order-service-state.factory';

export type OfficerDocument = {
  type: FileTypeEnum;
  file: File;
};

export type PostStatus =
  | 'IDLE'
  | 'SUBMITTING'
  | 'SUCCESS'
  | 'FINISHED'
  | 'ERROR';

export type ActionMode = 'New' | 'Edit' | 'Replace';

export abstract class OfficerRequestFormState {
  result$ = new Subject<Corporate | CompanyDirector>();

  public mode: ActionMode = 'New';

  public role: OfficerRoleEnum;

  public effectiveDate = new FormControl<Date>(
    DateTime.now().toJSDate(),
    Validators.required,
  );

  private _selectedOfficerType: OfficerTypeEnum = OfficerTypeEnum.Individual;
  get selectedOfficerType() {
    return this._selectedOfficerType;
  }
  set selectedOfficerType(val) {
    this._selectedOfficerType = val;
  }

  private _activeUser: User;
  get activeUser() {
    return this._activeUser;
  }
  set activeUser(val) {
    this._activeUser = val;
  }

  private _company: Company;
  get company() {
    return this._company;
  }
  set company(val) {
    this._company = val;
  }

  protected readonly availableOfficerTypes: OfficerTypeEnum[] = [
    OfficerTypeEnum.Individual,
    OfficerTypeEnum.Corporate,
    OfficerTypeEnum.Nominee,
  ];

  public serviceSupportedCountry: ServiceSupportedCountry;

  public jobOrderState = createOrderServiceState();

  public postStatus$ = new BehaviorSubject<PostStatus>('IDLE');

  public formInput: AbstractOfficerForm;

  public requireDocuments = [];

  public uploadedDocuments: OfficerDocument[] = [];

  public needDocument = false;

  public createdJob: JobType;

  public officerHelper = new OfficerHelper();

  public officerInputHelper = new OfficerInputHelpers();

  public existingOfficerState = new ExistingOfficerState();

  private _countryOfIncorporation: SupportedCountry;
  get countryOfIncorporation() {
    return this._countryOfIncorporation;
  }
  set countryOfIncorporation(val) {
    this._countryOfIncorporation = val;
    this.existingOfficerState.countryOfIncorporation = val;
  }

  public constructor() {
    //
  }

  public getAvailableOfficerTypes() {
    let filtered = this.availableOfficerTypes;

    if (this.serviceSupportedCountry.supportedCountry.country.code === 'SG') {
      filtered = this.availableOfficerTypes.filter(
        (o) => !(o === OfficerTypeEnum.Corporate),
      );
    }

    return filtered;
  }

  public createForm() {
    this.formInput = null;

    this.formInput = createOfficerForm(this.selectedOfficerType);

    this.formInput.createOfficerForm();

    // this.generateRequireDocuments();
  }

  protected generateRequireDocuments() {
    switch (this.selectedOfficerType) {
      case OfficerTypeEnum.Corporate:
        this.requireDocuments = [
          FileTypeEnum.CertificateOfIncorporation,
          FileTypeEnum.CertificateOfIncumbency,
          FileTypeEnum.ArticlesOfAssociation,
        ];

        this.needDocument = true;

        break;

      case OfficerTypeEnum.Individual:
        this.requireDocuments = [
          FileTypeEnum.Passport,
          FileTypeEnum.ProofOfAddress,
        ];

        this.needDocument = true;

        break;

      default:
        this.needDocument = false;
        this.requireDocuments = [];
    }
  }

  public fileChanged(event: DropzoneFileChangedEvent, type: FileTypeEnum) {
    if (event.type === DropzoneAction.Added) {
      this.uploadedDocuments.push({
        type,
        file: event.files[0],
      });
    } else {
      const index = this.uploadedDocuments.findIndex(
        (f) => f.file === event.files[0],
      );

      if (index > -1) {
        this.uploadedDocuments.splice(index, 1);
      }
    }
  }

  public isFormValid(): boolean {
    if (
      this.needDocument &&
      (this.uploadedDocuments.length === 0 ||
        this.uploadedDocuments.length !== this.requireDocuments.length)
    ) {
      return false;
    }

    if (!this.activeUser.isCustomer() && this.effectiveDate.invalid) {
      return false;
    }

    return !this.formInput.officerForm.invalid;
  }

  public individualOfficerForm() {
    return (
      (this.formInput.officerForm as FormGroup<OfficerIndividualFormType>) ||
      null
    );
  }

  public corporateOfficerForm() {
    return (
      (this.formInput.officerForm as FormGroup<OfficerCorporateFormType>) ||
      null
    );
  }

  public normalizeExistingOfficerChoices(): this {
    this.existingOfficerState.normalizeExistingOfficers();

    return this;
  }

  public resetRequestForm(): void {
    this.formInput.createOfficerForm();

    this.existingOfficerState.reset();

    this.scheduleOfficerFormState(true);
  }

  protected setOfficerPersonOfficerData(officer: OfficerPerson): void {
    this.patchOfficerPersonFormValue(officer);
  }

  protected setOfficerCompanyOfficerData(officer: OfficerCompany): void {
    this.patchOfficerCompanyFormValue(officer);
  }

  public applyExistingOfficerSelection(existingOfficer: ExistsOfficerType): this {
    this.createForm();

    this.existingOfficerState.selectedOfficerId = existingOfficer.id;

    const officer =
      this.existingOfficerState.findSelectedOfficer(existingOfficer);

    this.setExistingOfficerToForm(officer);

    return this;
  }

  public applyLookupOfficerSelection(officer: OfficerType): this {
    this.createForm();

    this.existingOfficerState.selectedOfficerId = officer.id;

    this.setExistingOfficerToForm(officer);

    return this;
  }

  protected scheduleOfficerFormState(isEnabled: boolean): void {
    Promise.resolve().then(() => {
      if (!this.formInput?.officerForm) {
        return;
      }

      if (isEnabled) {
        this.formInput.officerForm.enable();
      } else {
        this.formInput.officerForm.disable();
      }
    });
  }

  protected patchOfficerPersonFormValue(
    officer:
      | OfficerPerson
      | OfficerPersonType
      | null
      | undefined,
  ): void {
    const form = this.formInput.officerForm as FormGroup<OfficerIndividualFormType>;
    const address = officer?.address;
    const identification = officer?.identification as
      | OfficerPerson['identification']
      | OfficerPersonType['identification']
      | null
      | undefined;

    form.patchValue({
      salutation: officer?.salutation ?? null,
      firstName: officer?.firstName ?? '',
      lastName: officer?.lastName ?? '',
      countryId: address?.country?.id ?? '',
      email: officer?.email ?? '',
      phone: address?.phone ?? '',
      address: address?.address ?? '',
      apartment: address?.apartment ?? '',
      zip: address?.zip ?? '',
      city: address?.city ?? '',
      state: address?.state ?? '',
      identification: {
        officerIdentificationTypeId:
          identification?.officerIdentificationType?.id ?? '',
        nationalityId: identification?.nationality?.id ?? '',
        identificationNumber: identification?.identificationNumber ?? '',
        expiryDate: this.normalizeDateValue(identification?.expiryDate),
      },
    });
  }

  protected patchOfficerCompanyFormValue(
    officer:
      | OfficerCompany
      | OfficerCompanyType
      | null
      | undefined,
  ): void {
    const form = this.formInput.officerForm as FormGroup<OfficerCorporateFormType>;
    const address = officer?.address;
    const representativeProfile =
      (officer?.representative as OfficerType | OfficerCompany['representative'] | undefined)
        ?.officerProfile as OfficerPerson | OfficerPersonType | undefined;

    form.patchValue({
      name: officer?.name ?? '',
      registrationNumber: officer?.registrationNumber ?? '',
      registrationDate: this.normalizeDateValue(officer?.registrationDate),
      website: officer?.website ?? '',
      email: officer?.email ?? '',
      countryId: officer?.country?.id ?? '',
      address: {
        address: address?.address ?? '',
        apartment: address?.apartment ?? '',
        phone: address?.phone ?? '',
        zip: address?.zip ?? '',
        city: address?.city ?? '',
        state: address?.state ?? '',
        countryId: address?.country?.id ?? '',
      },
    });

    if (representativeProfile) {
      const representativeForm = form.controls.representative;
      const currentForm = this.formInput.officerForm;

      this.formInput.officerForm = representativeForm as unknown as FormGroup;
      this.patchOfficerPersonFormValue(representativeProfile);
      this.formInput.officerForm = currentForm;
    }
  }

  private normalizeDateValue(value: unknown): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = DateTime.fromISO(value);

      return parsed.isValid ? parsed.toJSDate() : null;
    }

    return null;
  }

  /**
   * Abstract function
   */

  public abstract setEditData(data: any): this;

  public abstract setServiceSupportedCountry(
    services: ServiceSupportedCountry[],
  ): this;

  protected abstract setExistingOfficerToForm(officer: OfficerType): void;

  public abstract getFormValue(): any;

  public abstract submitForm(): any;
}
